import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { SelectOption } from '@rtkwlf/fenrir-react';
import { HStack, Select, TextField } from '@rtkwlf/fenrir-react';
import { faSearch } from '@rtkwlf/pro-solid-svg-icons/faSearch';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { useScannerDetails } from '@apis/rendall/queries/use-scanner-details/use-scanner-details';
import { FieldWrapper } from '@packages/components/field-wrapper/field-wrapper';
import { DEBOUNCE_TIME_MEDIUM } from 'ae-constants/constants';

type Props = {
  handleFilterType: (selectOption: SelectOption | null) => void;
  handleFilterQuery: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type: string;
};

export enum ScanExclusionColumns {
  IP_RANGE = 'ipRange',
  PORTS = 'ports',
}

const filterOptions = [
  {
    text: 'IP Address/Range',
    value: ScanExclusionColumns.IP_RANGE,
  },
  {
    text: 'Ports',
    value: ScanExclusionColumns.PORTS,
  },
];

export const TableFilterRow = ({
  handleFilterType,
  handleFilterQuery,
  type,
}: Props) => {
  const { t } = useTranslation('telemetryScannerConfig');

  const { scannerId = '' } = useParams();
  const { data: hasPortExemptionCapability } = useScannerDetails(scannerId, {
    select: (data) => data.capabilities.includes('port_scan_exemption'),
  });

  const debouncedFilterQueryChange = useDebouncedCallback(
    handleFilterQuery,
    DEBOUNCE_TIME_MEDIUM,
  );

  const debouncedFilterTypeChange = useDebouncedCallback(
    handleFilterType,
    DEBOUNCE_TIME_MEDIUM,
  );

  return (
    <HStack
      width="full"
      backgroundColor="widgetSecondary"
      padding="default"
      borderRadius="default"
    >
      <HStack>
        {hasPortExemptionCapability && (
          <FieldWrapper
            label={t('scanExclusion.searchColumns')}
            field={
              <Select
                isClearable={false}
                options={filterOptions}
                value={type}
                onSelection={debouncedFilterTypeChange}
              />
            }
          />
        )}

        <FieldWrapper
          label={t('components:filter.search')}
          field={
            <TextField
              type="text"
              placeholder={t('components:filter.placeholder.search')}
              iconRight={<FontAwesomeIcon icon={faSearch} />}
              onChange={debouncedFilterQueryChange}
            />
          }
        />
      </HStack>
    </HStack>
  );
};
